import { useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import React from 'react';
import { AuthContext } from '../contexts/auth-context';
import { resetAppAccess } from '../utils/app-access';
import { captureError } from '../utils/capture-error';
import { markSessionExpired } from '../utils/session-expired';

/**
 * Sign out and sign in again, shared by the user menu, the maintenance
 * banner, the session-expired dialog and the System Screens.
 */
export function useSessionActions() {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // Hooks
  const queryClient = useQueryClient();

  /**
   * Sign out of Cognito, then clear cached data. The gate shows "Signing you
   * out…" from the start, so the app doesn't refetch and flash back while
   * Cognito finishes. While the session has expired, the Sign In page is
   * told so it can say so.
   */
  const signOut = React.useCallback(async () => {
    if (state.tokenError) {
      markSessionExpired();
    }
    dispatch({ type: 'SIGNING_OUT', payload: true });
    try {
      await Auth.signOut();
    } catch (error) {
      captureError({ data: { error } });
      if (state.tokenError) {
        // The session is already gone: go to Sign In regardless
        queryClient.removeQueries();
        window.location.assign('/sign-in?reason=expired');
      } else {
        // Still signed in: let them back into the app
        dispatch({ type: 'SIGNING_OUT', payload: false });
      }
      return;
    }
    queryClient.removeQueries();
    resetAppAccess();
  }, [dispatch, queryClient, state.tokenError]);

  /**
   * Back to the Cognito Hosted UI after the session expired. Cognito may
   * sign them straight back in if its own session is still alive.
   */
  const signInAgain = React.useCallback(async () => {
    try {
      await Auth.federatedSignIn();
    } catch (error) {
      captureError({ data: { error } });
      window.location.assign('/sign-in?reason=expired');
    }
  }, []);

  return { signOut, signInAgain };
}
